import React, { useState } from "react";
import { Modal, Flex } from "@jsq/ds/core";
import {
  Form,
  FormRadioGroup,
  FormRadioButton,
  FormTextInput,
  FormPercentInput,
  CancelButton,
  SubmitButton,
} from "@jsq/ds/forms";
import { NameFormFields } from "../common/NameFormFields";
import { createProfileValidationSchema, getInitialValues } from "./schema";
import { CreateProfileModalProps, ProfileType, ProfileFormValues } from "./types";

export const CreateProfileModal: React.FC<CreateProfileModalProps> = ({
  title,
  profileTypeLabel,
  showOwnershipPercentage,
  show,
  onClose,
  maxRemainingPercentage,
  minPercentage,
  onInformationSubmitted,
  initialValues = undefined,
}) => {
  const [profileType, setProfileType] = useState<ProfileType | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (values: ProfileFormValues) => {
    try {
      setIsSubmitting(true);
      await onInformationSubmitted(values);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal show={show} size="medium">
      <Modal.Header closeButton onHide={onClose}>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form<ProfileFormValues>
          initialValues={initialValues || getInitialValues()}
          yupValidationSchema={createProfileValidationSchema(
            showOwnershipPercentage,
            maxRemainingPercentage,
            minPercentage,
          )}
          onSubmit={handleSubmit}
          validateOnMount={false}
        >
          <Flex flexDirection="column" gap="16px">
            {/* Using display:none so that setProfileType is hit initially.
            We don't want to show the radio buttons because we don't want the user
            to change type of profile after it's already created */}
            <div style={{ display: initialValues ? "none" : "block" }}>
              <FormRadioGroup
                name="profileType"
                label={profileTypeLabel || "Profile Type"}
                onValueChanged={(value) => {
                  const profileTypeValue = value as ProfileType;
                  setProfileType(profileTypeValue);
                }}
              >
                <FormRadioButton value="Individual">Individual</FormRadioButton>
                <FormRadioButton value="Entity">Entity</FormRadioButton>
              </FormRadioGroup>
            </div>

            {profileType && (
              <>
                {profileType === "Individual" ? (
                  <NameFormFields />
                ) : (
                  <div>
                    <FormTextInput
                      label="Legal business name"
                      name="legalBusinessName"
                      isFullWidth
                      placeholder="This is usually the legal name of the management company/entity"
                    />
                  </div>
                )}

                {showOwnershipPercentage && (
                  <div>
                    <FormPercentInput label="Ownership percentage" name="ownershipPercentage" />
                  </div>
                )}
              </>
            )}
            <Flex flexDirection="row" gap="10px" justifyContent="flex-end">
              <CancelButton onClick={onClose}>Cancel</CancelButton>
              <SubmitButton isLoading={isSubmitting}>Save</SubmitButton>
            </Flex>
          </Flex>
        </Form>
      </Modal.Body>
    </Modal>
  );
};
